import React, { createRef } from "react";
import { connect } from "react-redux";
import { withRouter } from "react-router";

import L from "leaflet";
import { Map, TileLayer, Marker, Tooltip } from "react-leaflet";

import { create, remove, edit, cut, retrieve } from "../store/Map";
import MapService from "../services/MapService"
import "./LeafletMap.css"
import BlueprintGridUrl from "../images/blueprint-grid.png";
import WhiteMarkerIconUrl from "../images/marker-white.png";
import getUrlSegments from "../utilities/getUrlSegments";
import "leaflet-textlayer/dist/leaflet-textlayer.css";
import "../leaflet-extensions";

import "@imbick/leaflet-geoman-free";
import "@imbick/leaflet-geoman-free/dist/leaflet-geoman.css";

const parseMapUrlQuery = (location) => {
    if (!location || !location.pathname)
        return null;

    const segments = getUrlSegments(location.pathname);

    if (segments.length === 1)
        return null; //no query found

    return segments
        .slice(1)
        .join('/');
};

export class LeafletMap
    extends React.Component {

    constructor(props) {
        super(props);
        this.mapRef = createRef();

        this.pathOptions = {
            color: "white",
            fillColor: "#5f8bab",
            fillOpacity: 0.4,
            weight: 1.5
        };
        this.pmControlOptions = {
            position: "topright",
            drawPolyline: true,
            drawRectangle: true,
            cutPolygon: true,
            //don't currently support circular geometry
            drawCircleMarker: false,
            drawCircle: false
        };
        this.textEditorOptions = {
            editor: {
                toolbar: {
                    buttons: [
                        'bold',
                        'italic',
                        //{
                        //    name: 'h1',
                        //    action: 'append-h2',
                        //    aria: 'header type 1',
                        //    tagNames: ['h2'],
                        //    contentDefault: '<b>H1</b>',
                        //    classList: ['custom-class-h1'],
                        //    attrs: {
                        //        'data-custom-attr': 'attr-value-h1'
                        //    }
                        //},
                        'justifyCenter',
                        "justifyLeft",
                        "justifyCenter",
                        "justifyRight"
                    ]
                }
            }
        }

        this.retrieve = this.props.retrieve;
    }

    isAuthorised() {
        if (!this.props.user)
            return false;
        return this.props.user.profile.sub === this.props.mapAuthorId;
    }
    async persist() {
        if (!this.isAuthorised())
            return;
        await MapService.persist(this.props.query, this.toGeoJson());
    }
    blockTextLayerMode() {
        this._blockTextLayerMode = true;
    }
    create(eventArgs) {
        if (eventArgs.shape === "Marker") {
            eventArgs.marker.addEventListeners(this);
        } else /*if (eventArgs.shape === "Polygon")*/ {
            eventArgs.layer.addEventListeners(this);
        }

        this.persist();
    }
    remove(eventArgs) {
        this._layerGroup.removeLayer(eventArgs.layer);
        this.persist();
    }
    edit(eventArgs) {
        //if (eventArgs.layer.bringToFront)
        //    eventArgs.layer.bringToFront();

        this.persist();
    }
    cut(eventArgs) {
        eventArgs.layer.addEventListeners(this);
        this.persist();
    }
    editText(eventArgs) {

        if (!this._isThrottlingTextEdit) {
            setTimeout(() => {
                this._isThrottlingTextEdit = false;
                this.persist();
            },
                1000);
        }
        this._isThrottlingTextEdit = true;
    }
    addTextLayer(latlng, text) {
        text = text ? text : "Text";
        const layer = L.textLayer(text, latlng, this.textEditorOptions)
            .addTo(this.leafletElement);
        this.leafletElement.fire("pm:create", { layer });
    }
    addTextLayerListener = (e) => {
        if (this._blockTextLayerMode) {
            this._blockTextLayerMode = false;
            return;
        }
        this.addTextLayer(e.latlng);
    }
    toggleAddTextLayerMode(enabled) {
        if (enabled) {
            L.DomUtil.addClass(this.leafletElement.getContainer(), "addtextlayermode-enabled");
            this.leafletElement.on("click", this.addTextLayerListener);
        } else {
            L.DomUtil.removeClass(this.leafletElement.getContainer(), "addtextlayermode-enabled");
            this.leafletElement.off("click", this.addTextLayerListener);
        }
    }
    toggleTextEditing(enabled) {
        this.leafletElement.eachLayer((layer) => {
            if (layer instanceof L.TextLayer) {
                if (enabled)
                    layer.disable();
                else layer.enable();
            }
        });
    }
    toGeoJson() {
        var features = [];

        this.leafletElement.eachLayer((layer) => {
            if (!layer.isGeometryLayer() || layer.isTemporaryLayer())
                return;

            layer.addFeatureProperties();
            features.push(layer.toGeoJSON());
        });

        return features;
    }
    clearLayers() {
        this.leafletElement.eachLayer((layer) => {
            if (!layer.isGeometryLayer() || layer.isTemporaryLayer())
                return;

            this.leafletElement.removeLayer(layer);
        });
    }
    createLayerGroupFromGeoJson() {
        this.clearLayers();

        this._layerGroup = L.geoJSON(this.props.geoJson, {
            style: this.pathOptions
        });
        this._layerGroup.addTo(this.leafletElement);
        const self = this;
        this._layerGroup.eachLayer((layer) => {
            const isTextLayer = layer.feature.properties && layer.feature.properties.text;
            if (isTextLayer) {
                //todo define text options
                this._layerGroup.removeLayer(layer);
                const textLayer = L.TextLayer.fromFeatureLayer(layer);
                textLayer.addTo(this._layerGroup);

                //delay here required to allow the editor toolbar to update the text before the event fires (losing the formatting)
                //need to figure out how to prevent the rerender.
                textLayer.editor.subscribe("editableBlur", (e) => setTimeout(self.edit(e), 1000));
                textLayer.editor.subscribe("editableInput", (e) => self.editText(e));
            }
            layer.addEventListeners(this);
        });
    }
    componentDidUpdate(prevProps) {
        const geoJsonHasChanged = this.props.geoJson !== prevProps.geoJson;
        if (geoJsonHasChanged) {
            this.createLayerGroupFromGeoJson();
            //todo this.leafletElement.fitWorld();
        }
    }
    componentDidMount() {
        this.retrieve(this.props.query);
        this.initLeafletElement();
    }

    onZoom() {
        //may need to use css rather than style, as it doesn't handle nested elements like H2
        //may need to 'init' layers to a certain zoom level
        var zoomLevel = this.leafletElement.getZoomPercentage();
        this.leafletElement.eachLayer((layer) => {
            if (!(layer instanceof L.TextLayer))
                return;

            layer.tooltip.getElement().style.fontSize = (zoomLevel) + "%";
        });
    }
    initLeafletElement() {
        this.leafletElement = this.mapRef.current.leafletElement;

        this.leafletElement.pm.Draw.setPathOptions(this.pathOptions);
        this.leafletElement.pm.Draw.options.templineStyle = this.pathOptions;
        this.leafletElement.pm.Draw.options.hintlineStyle = { dashArray: [5, 5], ...this.pathOptions };

        this.leafletElement.on("pm:create", (e) => { this.create(e); });
        this.leafletElement.on("pm:remove", (e) => { this.remove(e); });
        this.leafletElement.on("pm:cut", (e) => { this.cut(e); });
        this.leafletElement.on("pm:globaldragmodetoggled", (e) => this.toggleTextEditing(e.enabled));
        this.leafletElement.on("pm:globalremovemodetoggled", (e) => this.toggleTextEditing(e.enabled));
        this.leafletElement.on("pm:merge", (e) => this.edit(e));
        
        //this.leafletElement.on("zoomend", (e) => this.onZoom(e));

        const addTextControl = this.leafletElement.pm.Toolbar.createCustomControl({
            name: "text",
            title: "Add Text",
            className: "addTextButton",
            actions: ["cancel"],
            onClick: () => { },
            afterClick: (e) => this.toggleAddTextLayerMode(addTextControl._button.toggleStatus),
            toggle: true
        });

        this.leafletElement.pm.addControls(this.pmControlOptions);

        L.Marker.prototype.options.icon = L.icon({
            iconUrl: WhiteMarkerIconUrl,
            iconAnchor: [16, 32]
        });
    }
    renderLoadingScreen() {
        this.clearLayers();
        return <Marker position={this.props.position} opacity="0">
                   <Tooltip permanent={true} direction="top">Loading...</Tooltip>
               </Marker>;
    }
    render() {
        const backgroundLayer =
            <TileLayer
                attribution='&copy; <a href="http://www.imbick.com">Imbick</a> v<a href="http://imbick.com/roadmaps/mapper">1.0-alpha</a>'
                url={BlueprintGridUrl}
            />;

        return (
            <Map
                center={this.props.position}
                zoom={this.props.zoom}
                className="map"
                ref={this.mapRef}
                crs={L.CRS.Simple}
            >
                {backgroundLayer}
                {this.props.isLoadingMap ? this.renderLoadingScreen() : <React.Fragment/>}
            </Map>
        );
    }
}

const mapStateToProps = (state, ownProps) => {
    return {
        geoJson: state.map.geoJson,
        position: state.map.position,
        zoom: state.map.zoom,
        query: state.map.id ? state.map.id : parseMapUrlQuery(ownProps.location), //ownProps.match.params.query ? Number(ownProps.match.params.query) : ,
        isLoadingMap: state.map.isLoading,
        mapAuthorId: state.map.userId,
        user: state.oidc.user
    };
};

const mapDispatchToProps = {
    create,
    remove,
    edit,
    cut,
    retrieve
};

export default withRouter(connect(
    mapStateToProps,
    mapDispatchToProps
)(LeafletMap));